/**
 * The pure core behind "a plan is a TEMPLATE" (ADR-0073 licence overhaul).
 *
 * Two jobs, both deliberately dependency-free so they can be unit-tested against the
 * backend's own rules rather than against a rendered form:
 *
 * 1. **The derived window.** The mint form asks for a START date and a CADENCE; the end
 *    date follows. This mirrors `hub.licensing.issuer.period_end` / `_add_months` so the
 *    operator sees the SAME date the issuer will record — the backend stays the authority
 *    (it derives it again on its own), this is the preview.
 * 2. **The overrides diff.** A per-licence customisation is stored as the DELTA over the
 *    plan snapshot, never as a full copy: sending the whole edited blob as `overrides`
 *    would freeze every untouched axis at today's plan value, so a later plan correction
 *    would silently stop reaching licences nobody meant to customise.
 */
import type { EntitlementsCatalog, PlanEntitlements } from "./platform";

export type BillingPeriod = "monthly" | "annual" | "custom";

/** Days in a given month (1-indexed), Gregorian. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * `yyyy-mm-dd` + N calendar months, CLAMPED to the target month's last day — the twin of
 * the backend's `_add_months`. Clamping is the point: a licence sold on the 31st renews on
 * the 28th/30th instead of overflowing into the next month (which would drift a monthly
 * cycle forward a day at a time).
 */
export function addMonths(date: string, months: number): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const day = Math.min(d, daysInMonth(year, month));
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * The end of a billing window, or `null` for `custom` — which has NO derivation, exactly
 * like the issuer, so the form asks for an explicit date instead of inventing a duration.
 */
export function deriveEndDate(startDate: string, period: BillingPeriod): string | null {
  if (period === "monthly") return addMonths(startDate, 1);
  if (period === "annual") return addMonths(startDate, 12);
  return null;
}

/** The plan snapshot ⊕ overrides — what the licence will actually grant. */
export function mergeEntitlements(
  base: PlanEntitlements | null | undefined,
  overrides: Partial<PlanEntitlements> | null | undefined,
): PlanEntitlements {
  return {
    features: { ...(base?.features ?? {}), ...(overrides?.features ?? {}) },
    limits: { ...(base?.limits ?? {}), ...(overrides?.limits ?? {}) },
  };
}

/**
 * A full entitlements blob for the catalog, seeded from `base` and falling back to each
 * axis's own default — so the editor renders EVERY axis (including ones the plan never
 * mentioned) rather than hiding the axes an operator is most likely to want to add.
 */
export function fullEntitlements(
  catalog: EntitlementsCatalog,
  base: PlanEntitlements | null | undefined,
): PlanEntitlements {
  const features: Record<string, boolean> = {};
  for (const a of catalog.features) {
    features[a.key] = Boolean(base?.features?.[a.key] ?? a.default ?? false);
  }
  const limits: Record<string, number | null> = {};
  for (const a of catalog.limits) {
    const v = base?.limits?.[a.key];
    limits[a.key] = v === undefined ? (typeof a.default === "number" ? a.default : null) : v;
  }
  return { features, limits };
}

/**
 * The DELTA of `edited` against the plan `base` — `null` when nothing was customised.
 *
 * Only axes that actually differ are emitted, so an untouched licence carries no overrides
 * at all and keeps inheriting the plan. Comparison is against the plan's OWN value where it
 * has one and the catalog default otherwise, because that is what the backend's `_merge`
 * would have produced for an absent key.
 */
export function diffOverrides(
  catalog: EntitlementsCatalog,
  base: PlanEntitlements | null | undefined,
  edited: PlanEntitlements,
): PlanEntitlements | null {
  const reference = fullEntitlements(catalog, base);
  const features: Record<string, boolean> = {};
  for (const a of catalog.features) {
    const v = Boolean(edited.features?.[a.key]);
    if (v !== reference.features[a.key]) features[a.key] = v;
  }
  const limits: Record<string, number | null> = {};
  for (const a of catalog.limits) {
    const v = edited.limits?.[a.key] ?? null;
    if (v !== reference.limits[a.key]) limits[a.key] = v;
  }
  const hasAny = Object.keys(features).length > 0 || Object.keys(limits).length > 0;
  return hasAny ? { features, limits } : null;
}

/** How many axes a customisation touches — drives the "N personalizzazioni" badge. */
export function overridesCount(overrides: PlanEntitlements | null): number {
  if (!overrides) return 0;
  return Object.keys(overrides.features ?? {}).length + Object.keys(overrides.limits ?? {}).length;
}
